import React, { useMemo, useRef, useEffect, useState } from 'react';
import { AgCharts } from 'ag-charts-react';
import { getAgChartTheme, getChartColors } from '../../../analytics/charts/chartTheme';
import { useTheme } from '../../../../stores/ThemeContext';
import { BarChart2 } from 'lucide-react';

const METRICS = [
  { key: 'total_pnl',    label: 'Total PnL',    format: (v) => `$${v.toFixed(2)}` },
  { key: 'win_rate',     label: 'Win Rate',     format: (v) => `${v.toFixed(1)}%` },
  { key: 'avg_pnl',     label: 'Avg PnL',      format: (v) => `$${v.toFixed(2)}` },
  { key: 'expectancy',  label: 'Expectancy',   format: (v) => `$${v.toFixed(2)}` },
];

const PnlBreakdownChart = ({ data = [], loading }) => {
  const { isDark } = useTheme();
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 280 });
  const [metric, setMetric] = useState('total_pnl');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width } = entry.contentRect;
        if (width > 0) setDimensions({ width, height: 280 });
      }
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  const colors = useMemo(() => getChartColors(isDark), [isDark]);
  const currentMetric = METRICS.find((m) => m.key === metric);

  const chartData = useMemo(() =>
    data.map((d) => ({
      playbook_name: d.playbook_name,
      value: d[metric] ?? 0,
    })),
  [data, metric]);

  const options = useMemo(() => ({
    theme: getAgChartTheme(isDark),
    width: dimensions.width || undefined,
    height: dimensions.height,
    data: chartData,
    series: [
      {
        type: 'bar',
        xKey: 'playbook_name',
        yKey: 'value',
        yName: currentMetric.label,
        itemStyler: ({ datum }) => ({
          fill: datum.value >= 0 ? colors.win : colors.loss,
          stroke: datum.value >= 0 ? colors.win : colors.loss,
        }),
        tooltip: {
          renderer: ({ datum }) => ({
            title: datum.playbook_name,
            content: `${currentMetric.label}: ${currentMetric.format(datum.value)}`,
          }),
        },
      },
    ],
    axes: [
      {
        type: 'category',
        position: 'bottom',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          fontSize: 11,
          rotation: data.length > 3 ? -30 : 0,
        },
      },
      {
        type: 'number',
        position: 'left',
        label: {
          color: isDark ? '#a1a1aa' : '#6b7280',
          formatter: ({ value }) => currentMetric.format(value),
          fontSize: 10,
        },
      },
    ],
    legend: { enabled: false },
  }), [chartData, dimensions, isDark, colors, currentMetric, data.length]);

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-4 animate-pulse">
        <div className="h-4 bg-muted rounded w-1/3 mb-4" />
        <div className="h-64 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-muted-foreground">Playbook PnL Breakdown</h3>
        </div>
        <div className="flex items-center gap-1">
          {METRICS.map((m) => (
            <button
              key={m.key}
              onClick={() => setMetric(m.key)}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                metric === m.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {data.length === 0 ? (
        <div className="h-64 flex flex-col items-center justify-center text-center">
          <BarChart2 className="w-8 h-8 text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">No journaled trades yet.</p>
          <p className="text-xs text-muted-foreground/60 mt-1">Journal trades with a playbook to see analytics.</p>
        </div>
      ) : (
        <div ref={containerRef} className="w-full">
          {dimensions.width > 0 && <AgCharts options={options} />}
        </div>
      )}
    </div>
  );
};

export default PnlBreakdownChart;
